import { log } from "helpers/logger"
import { HOURLY_INTERVALS, WEEKDAYS } from "./types"
import type {
  CreateScheduledWorkflowPayload,
  HourlyInterval,
  HourlySchedule,
  RecurringSchedule,
  ScheduledWorkflowSchedule,
  UpdateScheduledWorkflowPayload,
  Weekday,
  WeeklySchedule,
} from "./types"

export const WORKFLOW_DRAFTS_FIELD = "workflow_drafts"

export type WorkflowDraftAction = "create" | "update" | "archive" | "share"

export interface HourlyScheduleWire {
  type: "hourly"
  days: Weekday[]
  every_hours: HourlyInterval
  start: string
  end: string
  timezone: string
}

export type WorkflowScheduleWire = WeeklySchedule | HourlyScheduleWire

export interface WorkflowDraftPayloadWire {
  name?: string
  description?: string
  initial_message?: string
  skills?: string[]
  email_results?: boolean
  schedule?: WorkflowScheduleWire | null
  add?: string[]
  remove?: string[]
}

export interface WorkflowDraftWire {
  draft_id: string
  action: WorkflowDraftAction
  scheduled_workflow_id?: string
  config_version?: number
  payload?: WorkflowDraftPayloadWire
  unsupported_tools?: string[]
}

export interface CreateWorkflowDraft {
  draftId: string
  action: "create"
  payload: CreateScheduledWorkflowPayload & { draftId: string }
  unsupportedTools: string[]
}

export interface UpdateWorkflowDraft {
  draftId: string
  action: "update"
  scheduledWorkflowId: string
  configVersion: number
  payload: UpdateScheduledWorkflowPayload
}

export interface ArchiveWorkflowDraft {
  draftId: string
  action: "archive"
  scheduledWorkflowId: string
}

export interface ShareWorkflowDraft {
  draftId: string
  action: "share"
  scheduledWorkflowId: string
  add: string[]
  remove: string[]
}

export type WorkflowDraft = CreateWorkflowDraft | UpdateWorkflowDraft | ArchiveWorkflowDraft | ShareWorkflowDraft

type Field<T> = { kind: "absent" } | { kind: "value", value: T } | { kind: "invalid" }

const ABSENT = { kind: "absent" } as const
const INVALID = { kind: "invalid" } as const

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isWeekday(value: unknown): value is Weekday {
  return typeof value === "string" && (WEEKDAYS as readonly string[]).includes(value)
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== ""
}

export function isScheduleTime(value: unknown): value is string {
  return typeof value === "string" && TIME_PATTERN.test(value)
}

function isHourlyInterval(value: unknown): value is HourlyInterval {
  return HOURLY_INTERVALS.some((interval) => interval === value)
}

function narrowDays(value: unknown): Weekday[] | null {
  if (!Array.isArray(value) || value.length === 0 || !value.every(isWeekday)) return null
  if (new Set(value).size !== value.length) return null
  return [...value]
}

export function narrowWeeklySchedule(value: unknown): WeeklySchedule | null {
  if (!isRecord(value) || value.type !== "weekly") return null
  const { time, timezone } = value
  const days = narrowDays(value.days)
  if (days === null) return null
  if (!isScheduleTime(time)) return null
  if (!isNonEmptyString(timezone)) return null
  return { type: "weekly", days, time, timezone }
}

function hourlySchedule(value: Record<string, unknown>, everyHours: unknown): HourlySchedule | null {
  const { start, end, timezone } = value
  const days = narrowDays(value.days)
  if (days === null) return null
  if (!isHourlyInterval(everyHours)) return null
  if (!isScheduleTime(start) || !isScheduleTime(end) || end <= start) return null
  if (!isNonEmptyString(timezone)) return null
  return { type: "hourly", days, everyHours, start, end, timezone }
}

export function narrowHourlySchedule(value: unknown): HourlySchedule | null {
  if (!isRecord(value) || value.type !== "hourly") return null
  return hourlySchedule(value, value.everyHours)
}

export function narrowHourlyScheduleWire(value: unknown): HourlySchedule | null {
  if (!isRecord(value) || value.type !== "hourly") return null
  return hourlySchedule(value, value.every_hours)
}

export function narrowSchedule(value: unknown): RecurringSchedule | null {
  return narrowWeeklySchedule(value) ?? narrowHourlySchedule(value)
}

export function narrowScheduleWire(value: unknown): RecurringSchedule | null {
  return narrowWeeklySchedule(value) ?? narrowHourlyScheduleWire(value)
}

export function toScheduleWire(schedule: RecurringSchedule): WorkflowScheduleWire {
  if (schedule.type === "weekly") return { ...schedule, days: [...schedule.days] }
  const { everyHours, ...rest } = schedule
  return { ...rest, days: [...schedule.days], every_hours: everyHours }
}

function scheduleField(entry: Record<string, unknown>): Field<ScheduledWorkflowSchedule> {
  if (!("schedule" in entry) || entry.schedule === undefined) return ABSENT
  if (entry.schedule === null) return { kind: "value", value: null }
  const schedule = narrowScheduleWire(entry.schedule)
  return schedule === null ? INVALID : { kind: "value", value: schedule }
}

function stringField(entry: Record<string, unknown>, key: string, required: "non-empty" | "any"): Field<string> {
  const value = entry[key]
  if (value === undefined) return ABSENT
  if (typeof value !== "string") return INVALID
  if (required === "non-empty" && value.trim() === "") return INVALID
  return { kind: "value", value }
}

function stringListField(entry: Record<string, unknown>, key: string): Field<string[]> {
  const value = entry[key]
  if (value === undefined) return ABSENT
  if (!Array.isArray(value) || !value.every(isNonEmptyString)) return INVALID
  return { kind: "value", value: [...value] }
}

function booleanField(entry: Record<string, unknown>, key: string): Field<boolean> {
  const value = entry[key]
  if (value === undefined) return ABSENT
  if (typeof value !== "boolean") return INVALID
  return { kind: "value", value }
}

interface PayloadFields {
  name: Field<string>
  description: Field<string>
  initialMessage: Field<string>
  skills: Field<string[]>
  emailResults: Field<boolean>
  schedule: Field<ScheduledWorkflowSchedule>
}

function payloadFields(entry: Record<string, unknown>): PayloadFields | null {
  const fields: PayloadFields = {
    name: stringField(entry, "name", "non-empty"),
    description: stringField(entry, "description", "any"),
    initialMessage: stringField(entry, "initial_message", "non-empty"),
    skills: stringListField(entry, "skills"),
    emailResults: booleanField(entry, "email_results"),
    schedule: scheduleField(entry),
  }
  return Object.values(fields).some((field: Field<unknown>) => field.kind === "invalid") ? null : fields
}

function narrowCreatePayload(value: unknown, draftId: string): CreateWorkflowDraft["payload"] | null {
  if (!isRecord(value)) return null
  const fields = payloadFields(value)
  if (fields === null) return null
  const { name, description, initialMessage, skills, emailResults, schedule } = fields
  if (name.kind !== "value" || initialMessage.kind !== "value" || skills.kind !== "value") return null
  return {
    name: name.value,
    ...(description.kind === "value" && { description: description.value }),
    initialMessage: initialMessage.value,
    skills: skills.value,
    ...(schedule.kind === "value" && { schedule: schedule.value }),
    ...(emailResults.kind === "value" && { emailResults: emailResults.value }),
    draftId,
  }
}

function narrowUpdatePayload(value: unknown, configVersion: number): UpdateScheduledWorkflowPayload | null {
  if (!isRecord(value)) return null
  const fields = payloadFields(value)
  if (fields === null) return null
  if (Object.values(fields).every((field: Field<unknown>) => field.kind === "absent")) return null
  const { name, description, initialMessage, skills, emailResults, schedule } = fields
  return {
    configVersion,
    ...(name.kind === "value" && { name: name.value }),
    ...(description.kind === "value" && { description: description.value }),
    ...(initialMessage.kind === "value" && { initialMessage: initialMessage.value }),
    ...(skills.kind === "value" && { skills: skills.value }),
    ...(emailResults.kind === "value" && { emailResults: emailResults.value }),
    ...(schedule.kind === "value" && { schedule: schedule.value }),
  }
}

const MAX_SHARE_NAME_CHARS = 320
const MAX_SHARE_NAMES = 50

function isShareName(value: unknown): value is string {
  return isNonEmptyString(value) && value.length <= MAX_SHARE_NAME_CHARS
}

function narrowShareNames(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > MAX_SHARE_NAMES || !value.every(isShareName)) return null
  return [...value]
}

function narrowSharePayload(value: unknown): Pick<ShareWorkflowDraft, "add" | "remove"> | null {
  if (!isRecord(value)) return null
  const add = narrowShareNames(value.add)
  const remove = narrowShareNames(value.remove)
  if (add === null || remove === null || add.length + remove.length === 0) return null
  return { add, remove }
}

function isConfigVersion(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
}

export function narrowWorkflowDraft(value: unknown): WorkflowDraft | null {
  if (!isRecord(value)) return null
  const { draft_id: draftId, action, scheduled_workflow_id: scheduledWorkflowId, config_version: configVersion } = value
  if (!isNonEmptyString(draftId)) return null

  if (action === "create") {
    const payload = narrowCreatePayload(value.payload, draftId)
    const unsupportedTools = stringListField(value, "unsupported_tools")
    if (payload === null || unsupportedTools.kind === "invalid") return null
    return { draftId, action, payload, unsupportedTools: unsupportedTools.kind === "value" ? unsupportedTools.value : [] }
  }

  if (!isNonEmptyString(scheduledWorkflowId)) return null

  if (action === "archive") return { draftId, action, scheduledWorkflowId }

  if (action === "share") {
    const payload = narrowSharePayload(value.payload)
    return payload === null ? null : { draftId, action, scheduledWorkflowId, ...payload }
  }

  if (action === "update") {
    if (!isConfigVersion(configVersion)) return null
    const payload = narrowUpdatePayload(value.payload, configVersion)
    return payload === null ? null : { draftId, action, scheduledWorkflowId, configVersion, payload }
  }

  return null
}

export function narrowWorkflowDrafts(value: unknown): WorkflowDraft[] {
  if (!Array.isArray(value)) return []
  const drafts = value
    .map(narrowWorkflowDraft)
    .filter((draft): draft is WorkflowDraft => draft !== null)
  if (drafts.length < value.length) log(new Error(`${value.length - drafts.length} of ${value.length} workflow drafts do not match the contract`))
  return drafts
}
